import React, { useEffect } from 'react';
import { useTheme } from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Notification, NotificationType, Text } from 'edenred-ui';
import { useDispatch, useSelector } from 'react-redux';

import { PaymentMethod } from '@epi-forms/PaymentMethod/PaymentMethod';
import { BoxOval } from '@epi-shared/components';
import { FormFields } from '@epi-forms/helpers';
import { OrderType } from '@epi-constants/orderType';
import { PaymentMethods } from '@epi-constants/paymentMethods';
import { usePaymentMethodOptions } from '@epi-pages/TransferPage/LoadCompanyAccount/hooks';
import { IPaymentMethodOptions } from '@epi-models/containers/LoadCompanyAccount';
import {
  DISABLED_METHODS,
  HIDDEN_METHODS
} from '@epi-constants/loadCompanyAccount';
import { getUserCompany } from '@epi-actions/api';
import { getUserCompanyPaymentMethod } from '@epi-selectors/api';
import { ExternalPaymentRadio } from '@epi-forms/PaymentMethod/ExternalPaymentRadio/ExternalPaymentRadio';

export const PaymentMethodOptions = ({
  submitCompleteForm,
  disabled,
  isPaymentProcessing
}: IPaymentMethodOptions) => {
  const { gray10, lightBlue10 } = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const defaultPaymentMethod = useSelector(getUserCompanyPaymentMethod);
  const {
    calculateTotalAmount,
    apiPaymentMethods,
    isLessThanMinValue,
    onlinePaymentMethods
  } = usePaymentMethodOptions();

  const method = watch(FormFields.PaymentMethod);

  const getErrorMessage = () => {
    if (!isLessThanMinValue()) {
      return t('forms.payment_method.maksuturva_error');
    }
  };

  useEffect(() => {
    dispatch(getUserCompany());
  }, []);

  useEffect(() => {
    if (method !== PaymentMethods.CreditCard) {
      setValue(FormFields.PaymentMethodCode, '');
    }
  }, [method]);

  return (
    <>
      <Box p={2} border={1} borderColor={gray10} mb={2}>
        <Box display="flex" alignItems="flex-start" px={8} pb={2} pt={8}>
          <BoxOval text="2" bgColor={lightBlue10} />
          <Box flex={1} pt={1}>
            <Text>{t('containers.deposit_money.payment_method_title')}</Text>
          </Box>
          <Box display="flex" alignItems="center" flex={1}>
            <PaymentMethod
              paymentMethods={apiPaymentMethods}
              onClickPaymentMethod={submitCompleteForm}
              disabled={disabled}
              creditCardFeePercent={0}
              amountValue={calculateTotalAmount()}
              disabledMethods={DISABLED_METHODS}
              hiddenMethods={HIDDEN_METHODS}
              isPaymentProcessing={isPaymentProcessing}
              orderType={OrderType.digital}
              isLoadCompanyAccountPage
              defaultPaymentMethod={defaultPaymentMethod}
              isMasterAccount={true}
            />
          </Box>
        </Box>
        {apiPaymentMethods.isError && <Box px={8}>{getErrorMessage()}</Box>}
        <Box px={8}>
          <Box
            sx={{
              display:
                method === PaymentMethods.Online && !apiPaymentMethods.isError
                  ? 'block'
                  : 'none'
            }}
          >
            <ExternalPaymentRadio
              isError={apiPaymentMethods.isError || isLessThanMinValue()}
              isLoading={apiPaymentMethods.isLoading}
              disabled={disabled}
              methods={onlinePaymentMethods}
              onClickPaymentMethod={submitCompleteForm}
              isProcessing={isPaymentProcessing}
            />
          </Box>
        </Box>
      </Box>
      {(method === PaymentMethods.EInvoice ||
        method === PaymentMethods.PdfInvoice) && (
        <Notification
          id="e-invoice-info"
          type={NotificationType.Important}
          text={t('containers.deposit_money.einvoice_notification')}
        />
      )}
    </>
  );
};
